import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowUpRight, MessageSquare, ChevronDown, Globe, Wifi, Cog, Building2, User, Target, Briefcase, Heart, BookOpen, FileText, Wrench, Calendar, TrendingUp, LifeBuoy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MarketingHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const location = useLocation();
  
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileCompanyOpen(false);
    setMobileResourcesOpen(false);
  };

  // Determine active page for highlighting
  const isActive = (path) => location.pathname === path;
  const isSolutionsActive = () => location.pathname.startsWith('/services') || location.pathname === '/our-services';
  const isCompanyActive = () => location.pathname.startsWith('/company') || location.pathname === '/philanthropy';
  const isResourcesActive = () => location.pathname.startsWith('/resources');

  const solutionSubpages = [
    { path: '/services/digital', name: 'Digital', icon: Globe, description: 'Websites, Apps & Growth' },
    { path: '/services/events', name: 'Events', icon: Wifi, description: 'Convention Technology' },
    { path: '/services/systems', name: 'Systems', icon: Cog, description: 'Automation & Consulting' },
    { path: '/solutions/website-rescue', name: 'Website Rescue', icon: LifeBuoy, description: 'Fix broken or outdated sites' },
    { path: '/solutions/business-turnaround', name: 'Business Turnaround', icon: RefreshCw, description: 'Fix what\'s costing you money' },
  ];

  const companySubpages = [
    { path: '/company/about', name: 'About Fearless IT', icon: Building2, description: 'Who we are & what we do' },
    { path: '/company/mission', name: 'Mission & Philosophy', icon: Target, description: 'Our values & approach' },
    { path: '/philanthropy', name: 'Philanthropy', icon: Heart, description: 'Giving back to community' },
  ];

  const resourcesSubpages = [
    { path: '/growth-audit', name: 'Free Growth Audit', icon: TrendingUp, description: 'Assess your digital presence' },
    { path: '/resources/guides', name: 'Guides & Playbooks', icon: BookOpen, description: 'Free business resources' },
    { path: '/our-work', name: 'Case Studies', icon: Briefcase, description: 'See our portfolio' },
    { path: '/our-pricing', name: 'Investment', icon: FileText, description: 'View digital pricing plans' },
    { path: '/faq', name: 'FAQs', icon: MessageSquare, description: 'Common questions answered' },
    { path: '/resources/consultation', name: 'Book Consultation', icon: Calendar, description: 'Schedule a call' },
  ];

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white border-b border-cool-gray sticky top-0 z-50" data-testid="marketing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/" onClick={closeMobileMenu}>
                <img
                  src="https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png"
                  alt="Fearless IT"
                  className="h-7 sm:h-8 cursor-pointer"
                  data-testid="logo-image"
                />
              </Link>
            </div>
            
            {/* Desktop Navigation - Centered Links */}
            <div className="hidden md:flex items-center justify-center flex-1 space-x-6">
              {/* Solutions Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesDropdownOpen(true)}
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <button 
                  className={`flex items-center gap-1 font-medium transition-colors duration-200 ${isSolutionsActive() ? 'text-electric-indigo font-semibold' : 'text-black hover:text-electric-indigo'}`}
                >
                  Solutions
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${servicesDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                  <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[240px]">
                    {solutionSubpages.map((service) => {
                      const IconComponent = service.icon;
                      return (
                        <Link
                          key={service.path}
                          to={service.path}
                          className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${isActive(service.path) ? 'bg-blue-50' : ''}`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className={`font-medium ${isActive(service.path) ? 'text-electric-indigo' : 'text-carbon-black'}`}>{service.name}</div>
                            <div className="text-xs text-slate-gray">{service.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Company Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setCompanyDropdownOpen(true)}
                onMouseLeave={() => setCompanyDropdownOpen(false)}
              >
                <button 
                  className={`flex items-center gap-1 font-medium transition-colors duration-200 ${isCompanyActive() ? 'text-electric-indigo font-semibold' : 'text-black hover:text-electric-indigo'}`}
                >
                  Company
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${companyDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Company Dropdown Menu */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${companyDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                  <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[280px]">
                    {companySubpages.map((page) => {
                      const IconComponent = page.icon;
                      return (
                        <Link
                          key={page.path}
                          to={page.path}
                          className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${isActive(page.path) ? 'bg-blue-50' : ''}`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className={`font-medium ${isActive(page.path) ? 'text-electric-indigo' : 'text-carbon-black'}`}>{page.name}</div>
                            <div className="text-xs text-slate-gray">{page.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Resources Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setResourcesDropdownOpen(true)}
                onMouseLeave={() => setResourcesDropdownOpen(false)}
              >
                <button 
                  className={`flex items-center gap-1 font-medium transition-colors duration-200 ${isResourcesActive() ? 'text-electric-indigo font-semibold' : 'text-black hover:text-electric-indigo'}`}
                >
                  Resources
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${resourcesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Resources Dropdown Menu */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${resourcesDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                  <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[280px]">
                    {resourcesSubpages.map((page) => {
                      const IconComponent = page.icon;
                      return (
                        <Link
                          key={page.path}
                          to={page.path}
                          className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${isActive(page.path) ? 'bg-blue-50' : ''}`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className={`font-medium ${isActive(page.path) ? 'text-electric-indigo' : 'text-carbon-black'}`}>{page.name}</div>
                            <div className="text-xs text-slate-gray">{page.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Chat Bubble Icon with Gradient Border */}
              <Link 
                to="/contact" 
                className="group relative p-2 chat-bubble-icon"
                data-testid="chat-bubble-icon"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 rounded-lg"></div>
                <div className="absolute inset-[2px] bg-white rounded-md group-hover:bg-gray-50 transition-colors"></div>
                <MessageSquare className="relative h-5 w-5 text-indigo-500 group-hover:text-indigo-600 transition-all group-hover:scale-110" />
              </Link>
              <Link to="/register">
                <Button className="gradient-fearless text-white hover:opacity-90" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-fearless-blue text-fearless-blue hover:bg-blue-50" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button - Custom Hamburger */}
            <button
              className="md:hidden p-2 flex flex-col justify-center items-end gap-1.5 w-10 h-10"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <span className="block w-7 h-0.5 bg-black rounded-full"></span>
              <span className="block w-7 h-0.5 bg-black rounded-full"></span>
              <span className="block w-4 h-0.5 bg-black rounded-full"></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Full Screen Slide from Right */}
      <div 
        className={`fixed inset-0 z-[100] md:hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={closeMobileMenu}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute inset-0 bg-white transform transition-transform duration-300 ease-in-out flex flex-col ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Header - Dark bar with logo and X */}
          <div className="bg-black px-5 py-5 flex items-center justify-between flex-shrink-0">
            <Link to="/" onClick={closeMobileMenu}>
              <img
                src="https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png"
                alt="Fearless IT"
                className="h-7 brightness-0 invert"
              />
            </Link>
            <button
              onClick={closeMobileMenu}
              className="text-white hover:text-gray-300 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-8 w-8" strokeWidth={1.5} />
            </button>
          </div>
          
          {/* Menu Items */}
          <div className="px-6 py-8 flex-1 bg-white flex flex-col overflow-y-auto">
            {/* Page Links */}
            <nav className="space-y-0">
              {/* Solutions Expandable Section */}
              <div className="border-b border-gray-200">
                <button 
                  className="flex items-center justify-between py-5 w-full group"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                >
                  <span className={`text-2xl font-semibold transition-colors ${isSolutionsActive() ? 'text-fearless-blue' : 'text-black group-hover:text-fearless-blue'}`}>Solutions</span>
                  <ChevronDown className={`h-6 w-6 text-black group-hover:text-fearless-blue transition-all ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mobile Solutions Submenu */}
                <div className={`overflow-hidden transition-all duration-300 ${mobileServicesOpen ? 'max-h-96 pb-4' : 'max-h-0'}`}>
                  {solutionSubpages.map((service) => {
                    const IconComponent = service.icon;
                    return (
                      <Link
                        key={service.path}
                        to={service.path}
                        className={`flex items-center gap-3 py-3 pl-4 group ${isActive(service.path) ? 'text-electric-indigo' : ''}`}
                        onClick={closeMobileMenu}
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className={`text-lg font-semibold ${isActive(service.path) ? 'text-electric-indigo' : 'text-black group-hover:text-fearless-blue'} transition-colors`}>{service.name}</div>
                          <div className="text-sm text-slate-gray">{service.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Company Expandable Section */}
              <div className="border-b border-gray-200">
                <button 
                  className="flex items-center justify-between py-5 w-full group"
                  onClick={() => setMobileCompanyOpen(!mobileCompanyOpen)}
                >
                  <span className={`text-2xl font-semibold transition-colors ${isCompanyActive() ? 'text-fearless-blue' : 'text-black group-hover:text-fearless-blue'}`}>Company</span>
                  <ChevronDown className={`h-6 w-6 text-black group-hover:text-fearless-blue transition-all ${mobileCompanyOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mobile Company Submenu */}
                <div className={`overflow-hidden transition-all duration-300 ${mobileCompanyOpen ? 'max-h-[500px] pb-4' : 'max-h-0'}`}>
                  {companySubpages.map((page) => {
                    const IconComponent = page.icon;
                    return (
                      <Link
                        key={page.path}
                        to={page.path}
                        className={`flex items-center gap-3 py-3 pl-4 group ${isActive(page.path) ? 'text-electric-indigo' : ''}`}
                        onClick={closeMobileMenu}
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className={`text-lg font-semibold ${isActive(page.path) ? 'text-electric-indigo' : 'text-black group-hover:text-fearless-blue'} transition-colors`}>{page.name}</div>
                          <div className="text-sm text-slate-gray">{page.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Resources Expandable Section */}
              <div className="border-b border-gray-200">
                <button 
                  className="flex items-center justify-between py-5 w-full group"
                  onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                >
                  <span className={`text-2xl font-semibold transition-colors ${isResourcesActive() ? 'text-fearless-blue' : 'text-black group-hover:text-fearless-blue'}`}>Resources</span>
                  <ChevronDown className={`h-6 w-6 text-black group-hover:text-fearless-blue transition-all ${mobileResourcesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mobile Resources Submenu */}
                <div className={`overflow-hidden transition-all duration-300 ${mobileResourcesOpen ? 'max-h-[500px] pb-4' : 'max-h-0'}`}>
                  {resourcesSubpages.map((page) => {
                    const IconComponent = page.icon;
                    return (
                      <Link
                        key={page.path}
                        to={page.path}
                        className={`flex items-center gap-3 py-3 pl-4 group ${isActive(page.path) ? 'text-electric-indigo' : ''}`}
                        onClick={closeMobileMenu}
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className={`text-lg font-semibold ${isActive(page.path) ? 'text-electric-indigo' : 'text-black group-hover:text-fearless-blue'} transition-colors`}>{page.name}</div>
                          <div className="text-sm text-slate-gray">{page.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
              
              <Link 
                to="/contact" 
                className="flex items-center justify-between py-5 border-b border-gray-200 group"
                onClick={closeMobileMenu}
              >
                <span className="text-2xl font-semibold text-black group-hover:text-fearless-blue transition-colors">Contact</span>
                <ArrowUpRight className="h-5 w-5 text-black group-hover:text-fearless-blue transition-colors" />
              </Link>
            </nav>
            
            {/* Spacer */}
            <div className="flex-1"></div>
            
            {/* Bottom Buttons */}
            <div className="space-y-3 pb-8">
              <Link to="/register" onClick={closeMobileMenu} className="block">
                <button className="w-full gradient-fearless text-white py-4 px-6 rounded-xl font-semibold text-lg">
                  Get Started
                </button>
              </Link>
              <Link to="/login" onClick={closeMobileMenu} className="block">
                <button className="w-full gradient-fearless text-white py-4 px-6 rounded-xl font-semibold text-lg">
                  Sign In
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for chat bubble animation */}
      <style>{`
        .chat-bubble-icon:hover .absolute:first-child {
          animation: gradient-pulse 1s ease-in-out infinite;
        }
        
        @keyframes gradient-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
      `}</style>
    </>
  );
};

export default MarketingHeader;
